import React, { useEffect, useState, useMemo, useRef } from 'react'
import { View, RefreshControl, ScrollView, Platform, Text } from 'react-native'
import { FlashList } from '@shopify/flash-list'
import CategoryItem from './CategoryItem'
import { useFetchCourses } from './hooks/useFetchCourses'
import { Loader } from '../customUtils/Components/Loader'
import LoadingOverlay from './LoadingOverlay'
import styles from './learningHomeStyles'
import { LEARNING_STATUS } from './constants'
import { subscribeProgressUpdate, getProgressVersion } from './progressBus'
import Constants from 'expo-constants'
import { useBreakpoint } from '@/redux/zustand/useDimensionStore'
import theme from '../../../theme'

/**
 * LearningHomeWeb - Versión simplificada de LearningHome para web
 * Omite funcionalidades específicas de FileSystem que no están disponibles en web
 */

const LearningHomeWeb = () => {
    const { data, isLoading, isFetching, reload, fetch_data, setIsLoading, setReloadFetchCourses } = useFetchCourses()
    const [refresing, setRefresing] = useState(false)
    const hasFetchedRef = React.useRef(false)
    const categories = data?.single?.categories || (Array.isArray(data) ? data : [])
    console.log('[LearningHomeWeb] data:', data)
    console.log('[LearningHomeWeb] categories:', categories)
    console.log('[LearningHomeWeb] isLoading:', isLoading, 'isFetching:', isFetching)
    const expoConfig = Constants.expoConfig || { slug: '' }
    const breakPoint = useBreakpoint()
    const isMobile = breakPoint === 'sm'
    // En web, necesitamos disparar fetch_data manualmente ya que no hay navigation focus listener
    // Usamos useRef para asegurar que solo se llame UNA VEZ
    useEffect(() => {
        console.log('[LearningHomeWeb] useEffect fetch_data triggered')
        if (hasFetchedRef.current) {
            console.log('[LearningHomeWeb] fetch_data already called, skipping')
            return
        }
        hasFetchedRef.current = true
        console.log('[LearningHomeWeb] calling fetch_data')
        fetch_data().catch((error) => {
            console.error('[LearningHomeWeb] fetch_data error:', error)
        })
    }, [fetch_data]) // Incluir fetch_data pero usar ref para prevenir múltiples llamadas

    // Escuchar eventos de progreso y forzar reload inmediato de cursos
    const lastProgressVersionRef = useRef(getProgressVersion())
    useEffect(() => {
        const unsubscribe = subscribeProgressUpdate((version) => {
            try {
                if (typeof version === 'number' && version !== lastProgressVersionRef.current) {
                    lastProgressVersionRef.current = version
                    setReloadFetchCourses()
                }
            } catch {}
        })
        return () => {
            try {
                unsubscribe && unsubscribe()
            } catch {}
        }
    }, [setReloadFetchCourses])

    const onRefresh = async () => {
        setRefresing(true)
        await fetch_data().then(() => {
            setRefresing(false)
            setIsLoading(false)
        })
    }

    // Memoize updatedCats to avoid recalculating on every render
    const updatedCats = useMemo(() => {
        console.log('[LearningHomeWeb] calculating updatedCats')
        if (!categories || categories.length === 0) return []
        if (expoConfig.slug != 'shell-somosprotagonistas' && expoConfig.slug != 'dots') {
            return categories
        }
        let pendingCourses = []
        categories.forEach((category) => {
            let courses = category.repository.filter((course) => course.status == LEARNING_STATUS.PENDIENTE)

            if (courses.length > 0) {
                pendingCourses = [...pendingCourses, ...courses]
            }
        })
        let filteredCourses = []
        for (let i = 0; i < pendingCourses.length; i++) {
            const course = pendingCourses[i]
            if (!filteredCourses.some((c) => c._id === course._id)) {
                filteredCourses.push(course)
            }
        }
        let newCategory = { _id: '-1', category_name: 'Cursos pendientes', category_index: 1, blocked_by: [], repository: [] }
        newCategory.repository = filteredCourses
        let allCategories = [...categories]
        if (newCategory.repository.length > 0) {
            allCategories.unshift(newCategory)
        }
        allCategories.sort((a, b) => {
            return a.category_index - b.category_index
        })
        return allCategories
    }, [categories, expoConfig.slug])

    return (
        <View style={styles.container}>
            {categories && categories.length > 0 && (
                <>
                    {categories.length > 1 ? (
                        <View style={{ flex: 1 }}>
                            {expoConfig.slug == 'branca-one' && (
                                <View
                                    style={{
                                        paddingHorizontal: 10,
                                        paddingTop: 10,
                                    }}
                                >
                                    <Text
                                        style={{
                                            fontSize: 32,
                                            fontWeight: 'bold',
                                            color: theme.colors.textPrimary,
                                            marginBottom: 5,
                                        }}
                                    >
                                        {'Academia Branca'}
                                    </Text>
                                    <Text
                                        style={{
                                            fontSize: 20,
                                            fontWeight: 'normal',
                                            color: theme.colors.textSecondary,
                                            paddingBottom: 10,
                                            borderBottomColor: theme.colors.lightGray,
                                            borderBottomWidth: 1,
                                            marginBottom: 23,
                                        }}
                                    >
                                        {'En esta sección vas a poder encontrar cursos para capacitarte y mejorar tu rendimiento.'}
                                    </Text>
                                </View>
                            )}
                            <FlashList
                                refreshControl={<RefreshControl refreshing={refresing} onRefresh={onRefresh} progressViewOffset={10} />}
                                data={updatedCats}
                                keyExtractor={(item) => item._id}
                                horizontal={false}
                                showsVerticalScrollIndicator={false}
                                estimatedItemSize={20}
                                extraData={reload}
                                renderItem={({ item }) => (
                                    <CategoryItem data={item} categories={updatedCats} reload={reload} single={false} isMobile={isMobile} />
                                )}
                            />
                        </View>
                    ) : (
                        <CategoryItem data={categories[0]} categories={updatedCats} reload={reload} single={true} isMobile={isMobile} />
                    )}
                </>
            )}

            <LoadingOverlay visible={!!(isLoading || isFetching)} message={'Cargando cursos…'} />
        </View>
    )
}

export default LearningHomeWeb
